import { Layout } from 'antd';
import { Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { openModal, selectOpenSubMenu, toggleSubMenu } from '../../modules/ui/store/ui-slice';
import { totpModalEnableId } from '../../modules/user/constants/constants';
import EnableTotpModal from '../../modules/user/components/enable-totp-modal/enable-totp-modal';
import HamburgerButton from '../../shared/components/hamburger-button/hamburger-button';
import PageSpinner from '../../shared/components/page-spinner/page-spinner';
import { useBreakpoint } from '../../shared/hooks/use-breakpoint';
import { useCheckTotpRequirements } from '../../shared/utils/check-totp-requirements';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import MainMenuDesktop from './components/main-menu-desktop/main-menu-desktop';
import useMenuItems from './hooks/use-menu-items';
import styles from './main-layout.module.css';

const MainLayout = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const openSubMenu = useAppSelector(selectOpenSubMenu);
  const [, activeMenuItem] = useMenuItems();
  const screens = useBreakpoint();
  const [_, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { i18n } = useTranslation();

  const isTotpRequiredButNotEnabled = useCheckTotpRequirements();

  useEffect(() => {
    if (isTotpRequiredButNotEnabled) {
      dispatch(openModal(totpModalEnableId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTotpRequiredButNotEnabled, location]);

  useEffect(() => {
    setSearchParams(
      existingSearchParams => {
        existingSearchParams.set('lang', i18n.language);
        return existingSearchParams;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, i18n.language]);

  return (
    <Layout className={styles.layout}>
      {screens.xl && (
        <div className={styles.mainMenuContainer}>
          <Suspense>
            <HamburgerButton
              changeIcon={false}
              extraMargin={true}
              buttonProps={{
                onClick: () => dispatch(toggleSubMenu()),
              }}
              isOpened={openSubMenu}
              isDisabled={!activeMenuItem?.subMenuContent}
            />
            <MainMenuDesktop />
          </Suspense>
        </div>
      )}

      <Layout>
        <Layout.Content className={styles.layoutContent}>
          <Suspense fallback={<PageSpinner />}>
            <Outlet />
          </Suspense>
        </Layout.Content>
      </Layout>
      <EnableTotpModal />
    </Layout>
  );
};

export default MainLayout;
